import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, remove, setQty, subtotal } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-background border-l border-border p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="editorial-heading text-3xl tracking-tight text-secondary text-left">
            Your Cart
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-3">
              <p className="overline text-muted-foreground">Empty</p>
              <p className="text-sm text-muted-foreground">
                Your cart is waiting. Browse the catalog to begin.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((it) => (
                <li key={it.product_id} className="flex gap-4 p-6" data-testid={`cart-item-${it.slug}`}>
                  <img src={it.image} alt={it.name} className="w-20 h-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary truncate">{it.name}</p>
                    <p className="text-xs text-muted-foreground mb-3">${it.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-border">
                        <button
                          onClick={() => setQty(it.product_id, it.quantity - 1)}
                          className="p-1.5 hover:bg-muted"
                          data-testid={`cart-decrement-${it.slug}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-sm" data-testid={`cart-qty-${it.slug}`}>
                          {it.quantity}
                        </span>
                        <button
                          onClick={() => setQty(it.product_id, it.quantity + 1)}
                          className="p-1.5 hover:bg-muted"
                          data-testid={`cart-increment-${it.slug}`}
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <button
                        onClick={() => remove(it.product_id)}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
                        data-testid={`cart-remove-${it.slug}`}
                      >
                        <Trash2 className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-secondary">
                    ${(it.price * it.quantity).toFixed(2)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border p-6 space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-secondary font-medium" data-testid="cart-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Shipping & tax calculated at checkout. Research use only.
            </p>
            <Button
              onClick={goToCheckout}
              data-testid="cart-checkout-button"
              className="w-full rounded-none bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground uppercase tracking-[0.18em] text-xs py-6"
            >
              Checkout
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
